/**
 * What's New popup — shown right after sign-in when the server says there is
 * an unseen FEATURE (see whatsNewStore.js / apps/api modules/whats-new/store.ts
 * for the show/suppress rule). Lists every entry (features and fixes) added
 * since this person last ticked "Don't show this again".
 */

import { useState } from 'react';
import Modal from '@/components/ui/Modal.jsx';
import { useWhatsNewStore } from '@/state/whatsNewStore.js';
import { useHelpStore } from '@/state/helpStore.js';

const KIND_META = {
  feature: { label: 'New', bg: 'rgba(99,102,241,0.14)', color: '#4338ca', icon: 'fa-sparkles' },
  fix: { label: 'Fixed', bg: 'rgba(16,185,129,0.14)', color: '#047857', icon: 'fa-wrench' }
};

function fmtDate(iso) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function WhatsNewModal() {
  const open = useWhatsNewStore((s) => s.open);
  const entries = useWhatsNewStore((s) => s.entries);
  const dismiss = useWhatsNewStore((s) => s.dismiss);
  const openHelp = useHelpStore((s) => s.openHelp);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!open) return null;

  function close() {
    dismiss(dontShowAgain);
    setDontShowAgain(false);
  }

  function viewFullHistory() {
    dismiss(dontShowAgain);
    setDontShowAgain(false);
    openHelp({ section: 'whats-new', articleId: 'whats-new--updates' });
  }

  /** Per-entry "Learn more" — same close-then-navigate pattern as Full history. */
  function learnMore(entry) {
    dismiss(dontShowAgain);
    setDontShowAgain(false);
    openHelp({ articleId: entry.helpArticleId });
  }

  return (
    <Modal
      open
      id="whatsNewModal"
      className="modal--whats-new"
      onClose={close}
      title="What's New in Omni Cloud"
      titleIcon="fa-gift"
      headerStyle={{ background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)' }}
      ariaLabel="What's New in Omni Cloud"
      width="560px"
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 12 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: 'var(--text-2)',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            Don't show this again
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="lg-btn lg-btn--glass" onClick={viewFullHistory}>
              <i className="fas fa-clock-rotate-left" aria-hidden="true" /> Full history
            </button>
            <button type="button" className="lg-btn lg-btn--green" onClick={close}>
              <i className="fas fa-check" aria-hidden="true" /> Got it
            </button>
          </div>
        </div>
      }
    >
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--muted)' }}>
        Here's what's changed in Omni Cloud since you last looked.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '52vh', overflowY: 'auto' }}>
        {entries.map((e) => {
          const meta = KIND_META[e.kind] ?? KIND_META.feature;
          return (
            <div
              key={e.id}
              style={{
                display: 'flex',
                gap: 12,
                padding: '12px 14px',
                borderRadius: 14,
                background: 'var(--hairline)',
                border: '1px solid var(--hairline-strong)'
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: meta.bg,
                  color: meta.color,
                  fontSize: 14
                }}
              >
                <i className={`fas ${meta.icon}`} aria-hidden="true" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 800,
                      letterSpacing: 0.3,
                      textTransform: 'uppercase',
                      color: meta.color,
                      background: meta.bg,
                      padding: '2px 8px',
                      borderRadius: 999
                    }}
                  >
                    {meta.label}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{fmtDate(e.date)}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', margin: '4px 0 2px' }}>{e.title}</div>
                <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.4 }}>{e.description}</div>
                {e.helpArticleId && (
                  <button
                    type="button"
                    onClick={() => learnMore(e)}
                    style={{
                      marginTop: 8,
                      padding: 0,
                      border: 'none',
                      background: 'none',
                      color: meta.color,
                      fontSize: 12.5,
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5
                    }}
                  >
                    Learn more <i className="fas fa-arrow-right" aria-hidden="true" style={{ fontSize: 10 }} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
